import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** Artyeto: id of the latest cart update, so an older count never wins. */
  #countRequest = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    // Artyeto: lens lines are nested under their frame, so the bubble counts top-level lines only.
    const request = ++this.#countRequest;

    event.promise
      ?.then(({ cart, detail }) => topLevelCount(event, detail, cart?.totalQuantity ?? detail?.itemCount))
      .then((itemCount) => {
        // A slow `/cart.js` fallback must not overwrite the count of a newer update.
        if (request !== this.#countRequest) return;

        this.renderCartBubble(itemCount ?? 0);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    // Artyeto: the cached value is the top-level count, same as the Liquid bubble.
    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(this.currentCartCount),
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      // Storage blocked: the bubble still shows the right count.
    }

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    let sessionStorageCount = null;
    try {
      sessionStorageCount = sessionStorage.getItem('cart-count');
    } catch (_) {
      return;
    }

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

/**
 * Artyeto: sums the quantities of top-level cart lines. Nested lines (lenses added with `parent_id`)
 * come back from the Ajax API with `parent_relationship: { parent_key }`.
 * @param {unknown} items - Cart items as returned by `/cart.js`.
 * @returns {number | undefined} Undefined when `items` is not a list, so callers can fall back.
 */
function countTopLevelItems(items) {
  if (!Array.isArray(items)) return undefined;

  return items.reduce((total, item) => {
    if (item?.parent_relationship?.parent_key) return total;
    return total + (Number(item?.quantity) || 0);
  }, 0);
}

/**
 * Artyeto: top-level count for a cart update. Every dispatcher in this theme resolves with the full
 * `/cart.js` items in `detail.items`. For any other dispatcher (apps, `Shopify.actions`), `/cart.js`
 * is fetched once per event and shared with `header-actions.js` through the event object; the total
 * quantity (which includes lens lines) is the last resort.
 * Keep in sync with the copy in `header-actions.js`.
 * @param {Event} event
 * @param {{ items?: unknown } | undefined} detail
 * @param {number | undefined} fallback - Count used when `/cart.js` fails.
 * @returns {Promise<number | undefined>}
 */
function topLevelCount(event, detail, fallback) {
  const fromDetail = countTopLevelItems(detail?.items);
  if (fromDetail !== undefined) return Promise.resolve(fromDetail);
  if (fallback === 0) return Promise.resolve(0);

  const shared = /** @type {Event & { artyetoTopLevelCount?: Promise<number | undefined> }} */ (event);
  shared.artyetoTopLevelCount ??= fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => countTopLevelItems(data?.items))
    .catch(() => undefined);

  return shared.artyetoTopLevelCount.then((count) => count ?? fallback);
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
